export type AdmissionMode = "AUTO" | "NEW" | "RETURNING";

/**
 * The create form offers "Auto / New / Returning". Auto sends nothing so the API defaults it
 * (new only for a student's first enrollment); the other two send an explicit flag.
 */
export function admissionModeToPayload(mode: AdmissionMode | undefined): {
  isNewAdmission?: boolean;
} {
  if (mode === "NEW") return { isNewAdmission: true };
  if (mode === "RETURNING") return { isNewAdmission: false };
  return {};
}

/** Strips UI-only fields and maps the admission mode; leaves everything else untouched. */
export function toEnrollmentCreatePayload<T extends { admissionMode?: AdmissionMode }>(
  data: T
): Omit<T, "admissionMode"> & { isNewAdmission?: boolean } {
  const { admissionMode, ...rest } = data;
  return { ...rest, ...admissionModeToPayload(admissionMode) };
}

export type YearOption = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isClosed?: boolean;
};
export type ClassOption = { id: string; name: string; sortOrder: number; isActive?: boolean };

/** The open year that starts right after the source year (the natural promotion target). */
export function suggestNextYear(years: YearOption[], fromYearId: string): YearOption | undefined {
  const from = years.find((y) => y.id === fromYearId);
  if (!from) return undefined;
  return years
    .filter((y) => y.startDate > from.startDate && !y.isClosed)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
}

/** The active class that sorts right after the source class. */
export function suggestNextClass(
  classes: ClassOption[],
  fromClassId: string
): ClassOption | undefined {
  const ordered = [...classes]
    .filter((c) => c.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  const index = ordered.findIndex((c) => c.id === fromClassId);
  return index >= 0 ? ordered[index + 1] : undefined;
}

export type PromotionPreview = {
  target: {
    fromAcademicYear: string;
    fromClass: string;
    toAcademicYear: string;
    toClass: string;
    enrolledOn: string;
  };
  eligible: Array<{ studentId: string; name: string; admissionNo: number }>;
  skipped: Array<{
    studentId: string;
    name: string | null;
    admissionNo: number | null;
    reason: string;
    reasonText: string;
  }>;
};

export type PromotionResult = {
  target: PromotionPreview["target"];
  created: number;
  skippedByConcurrentEnrollment: number;
  skipped: PromotionPreview["skipped"];
};

/** One-line outcome for the notification / result banner. */
export function summarizePromotion(result: PromotionResult): string {
  const { created, skipped, skippedByConcurrentEnrollment, target } = result;
  const parts = [
    `Promoted ${created} ${created === 1 ? "student" : "students"} to ${target.toClass} (${target.toAcademicYear}).`,
  ];
  const skippedTotal = skipped.length + skippedByConcurrentEnrollment;
  if (skippedTotal > 0) {
    parts.push(`${skippedTotal} skipped.`);
  }
  return parts.join(" ");
}
